import { createContext, type Dispatch, type ReactNode, type SetStateAction, useContext, useMemo, useState } from 'react';
interface LayoutOverrideContextType {
  displayHeader: boolean | null;
  setDisplayHeader: Dispatch<SetStateAction<boolean | null>>;
}
const LayoutOverrideContext = createContext<LayoutOverrideContextType | undefined>(undefined);
interface LayoutOverrideProviderProps {
  children: ReactNode;
}

/**
 * Allows overriding route specific layout configurations.
 *
 * Since layout is primarily controlled through `.../router/routes.tsx`, this context
 * allows overriding the current route's layout configuration.
 *
 * **!!! Important !!!** - always clear the override to `null` when finished:
 *
 * ```typescript
 *   useEffect(() => {
 *     setDisplayHeader(true);
 *     return () => setDisplayHeader(null);
 *   }, []);
 * ```
 */
export const LayoutOverrideProvider = ({
  children
}: LayoutOverrideProviderProps) => {
  const [displayHeader, setDisplayHeader] = useState<boolean | null>(null);
  const providerValue = useMemo(() => ({
    displayHeader,
    setDisplayHeader
  }), [displayHeader]);
  return <LayoutOverrideContext.Provider value={providerValue} data-sentry-element="unknown" data-sentry-component="LayoutOverrideProvider" data-sentry-source-file="layout-override-context.tsx">{children}</LayoutOverrideContext.Provider>;
};
export const useLayoutOverride = () => {
  const context = useContext(LayoutOverrideContext);
  if (context) return context;
  if (process.env.NODE_ENV === 'development') {
    throw new Error('useLayoutOverride must be used within a LayoutOverrideProvider');
  }
  return {
    displayHeader: null,
    setDisplayHeader: () => {}
  };
};